import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export abstract class Model {
  id!: number;
  createdAt!: Date;
  updatedAt!: Date;
  deletedAt?: Date;

  constructor(record?: any) {
    record = record || {};
    Object.assign(this, record);
  }

  toJson(): any {
    return { id: this.id };
  }
}

export class PageRequest {
  page: number;
  size: number;
  sortDirection: string;
  sortProperty: string;

  constructor(page?: number, size?: number, sortDirection?: string, sortProperty?: string) {
    this.page = page || 0;
    this.size = size || -1;
    this.sortDirection = sortDirection || 'asc';
    this.sortProperty = sortProperty || 'id';
  }

  toHttpParams(): HttpParams {
    let params = new HttpParams();
    params = params.append('page', this.page.toString());
    params = params.append('size', this.size.toString());
    if (this.sortDirection) params = params.append('sortDirection', this.sortDirection);
    if (this.sortProperty) params = params.append('sortProperty', this.sortProperty);
    return params;
  }

  toQueryParams(): any {
    let queryParams: any = {
      page: this.page,
      size: this.size
    };
    if (this.sortDirection) queryParams.sortDirection = this.sortDirection;
    if (this.sortProperty) queryParams.sortProperty = this.sortProperty;
    return queryParams;
  }
}

export interface Record<T> {
  id?: T;
}

export interface PaginationResult<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class NgxCrudService<T extends Record<U>, U> {
  protected _baseUrl: string;

  constructor(
    protected http: HttpClient,
    @Inject('config') protected config: { apiBaseUrl: string, [key: string]: any }
  ) {
    this._baseUrl = this.config.apiBaseUrl || '';
  }

  public search(params: any, pageRequest?: PageRequest): Observable<PaginationResult<T>> {
    if (!pageRequest)
      pageRequest = new PageRequest();
    let queryParams: any = pageRequest.toQueryParams();
    queryParams.q = params;
    return this.http.post<PaginationResult<T>>(this._baseUrl + 'search', queryParams, { responseType: 'json', withCredentials: true })
  }

  public list(): Observable<T[]> {
    return this.http.get<T[]>(this._baseUrl, { responseType: 'json', withCredentials: true });
  }

  public get(id: U): Observable<T> {
    return this.http.get<T>(this._baseUrl + id, { responseType: 'json', withCredentials: true });
  }

  public save(object: T) {
    if (object.id) {
      return this.edit(object);
    }
    return this.create(object);
  }

  public edit(object: T) {
    let id = object.id;
    let data = Object.assign({}, object);
    delete data.id;
    return this.http.put<T>(this._baseUrl + id, data, { responseType: 'json', withCredentials: true });
  }

  public create(object: T) {
    delete object.id;
    return this.http.post<T>(this._baseUrl, object, { responseType: 'json', withCredentials: true });
  }

  public delete(id: U) {
    return this.http.delete<any>(this._baseUrl + id, { responseType: 'json', withCredentials: true });
  }
}
